import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  BFIslandFooter,
  useDoubleTapHandler,
  useHeartAnimation,
} from '@buzzfeed/react-components';
import { getXsrfToken } from '../../../utils/xsrf';
import {
  triggerEnhancedWebviewFeature,
  isEnhancedWebviewFeatureSupported,
} from '@buzzfeed/bf-utils/lib/webview';
import { useGesture } from '@use-gesture/react';
import { useRouter } from 'next/router';
import { useTrackingContext, useViewImpression } from '../../../hooks';
import CommunityChallengeBanner from './CommunityChallengeBanner';
import FeedbackModal from './FeedbackModal';
import { useFeedbackModal } from './FeedbackModal/useFeedbackModal';
import { MODAL_STATES, ANIMATION_TIMINGS } from './constants';
import { getFilename } from './helpers';
import ZoomableImage from './ZoomableImage';
// import ImageFilter from './ImageFilter';
import styles from './artifact.module.scss';
import { RiffIcon } from '../../../icons/ChatIcons';
import { CreateStickerIcon } from '../../../icons/Icons';
import {
  OverflowActions,
  BFIslandDangerIcon,
} from '@buzzfeed/react-components';

const readBlobAsBase64 = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = typeof reader.result === 'string' ? reader.result : '';
      resolve(result.includes(',') ? result.split(',')[1] : result);
    };
    reader.onerror = () => reject(new Error('Failed to convert image to base64'));
    reader.readAsDataURL(blob);
  });

const buildCreateStickerSource = ({ contentObject, feedContent }) => {
  if (feedContent?.source !== 'gallery') {
    return undefined;
  }

  const contentObjectId = contentObject?.id;
  const objectTypeId = contentObject?.object_type?.id;

  if (!contentObjectId || !objectTypeId) {
    return undefined;
  }

  return {
    type: 'contentObject',
    data: {
      id: contentObjectId,
      object_type: {
        id: objectTypeId,
      },
    },
  };
};

export const Artifact = ({
  contentObject,
  // tracking info as provided by (app) container about the user/device/page
  trackingInfo: baseTrackingInfo,
  // isBFIslandApp,
  isWeb,
  openShare,
  setIsGatingModalOpen,
  feedContent,
}) => {
  const [likeCount, setLikeCount] = useState(0);
  const [hasLiked, setHasLiked] = useState(false);
  const [commentsCount, setCommentsCount] = useState(0);
  const [isCaptionExpanded, setIsCaptionExpanded] = useState(false);
  const [isContentReported, setIsContentReported] = useState(false);
  const { trackInternalLink, trackContentAction } = useTrackingContext();
  const router = useRouter();

  const ownerProfile = contentObject?.owner_profile || {};
  const type = contentObject?.content?.bfp?.data?.type || 'image_generator';
  const isResolved = contentObject?.content?.bfp?.data?.isResolved;
  const commentId = contentObject?.content?.single_comment?.id;

  const introImageUrl = getFilename(
    contentObject?.content?.bfp?.data?.intro?.card?.backgroundImage?.url
  );
  const artifactImageUrl = getFilename(
    contentObject?.content?.single_comment?.picture_url
  );
  const createStickerImageUrl = contentObject?.content?.single_comment?.picture_url;
  const isChallengeWinner = introImageUrl === artifactImageUrl;

  // Feedback modal hook
  // Simple blur state for modal animation - ONLY blur control
  const [shouldBlurContent, setShouldBlurContent] = useState(false);
  const [isImageAnimating, setIsImageAnimating] = useState(false);
  const [isImageAnimatingBack, setIsImageAnimatingBack] = useState(false);
  const [hasAnimationCompleted, setHasAnimationCompleted] = useState(false);

  const showReportButton = useMemo(() => {
    if (typeof window === 'undefined') {
      return false;
    }
    return isEnhancedWebviewFeatureSupported({
      feature: 'navigate',
      params: {
        path: '/webview/report',
        queryParams: {},
      },
      version: 1,
    });
  }, []);

  const [showCreateStickerButton, setShowCreateStickerButton] = useState(false);

  useEffect(() => {
    setShowCreateStickerButton(
      isEnhancedWebviewFeatureSupported({
        feature: 'createSticker',
        version: 1,
      })
    );
  }, []);

  const { shouldShow, isVisible, triggerModal, hideModal } = useFeedbackModal(
    hasAnimationCompleted
  );

  // Heart animation hook for double-tap like feedback
  const { trigger, portal } = useHeartAnimation();

  const handleLike = async (state) => {
    // Trigger heart animation immediately
    if (state?.event) {
      trigger(state.event.clientX, state.event.clientY);
    }

    // Only perform like operation if not already liked
    if (hasLiked) return;

    const likedContentIds =
      JSON.parse(localStorage.getItem('bfIsland_likedContentIds')) || [];
    const likedCommentIds =
      JSON.parse(localStorage.getItem('bfIsland_likedCommentIds')) || [];

    try {
      const response = await fetch(
        `/comments-api/v1/comments/${commentId}/reactions`,
        {
          method: 'POST',
          body: JSON.stringify({ reaction: 'love' }),
          headers: {
            accept: 'application/json',
            'Content-Type': 'application/json',
            'X-xsrftoken': getXsrfToken(),
          },
        }
      );
      if (response.ok) {
        setLikeCount(likeCount + 1);
        setHasLiked(true);
        localStorage.setItem(
          'bfIsland_likedContentIds',
          JSON.stringify([...likedContentIds, contentObject?.id])
        );
        localStorage.setItem(
          'bfIsland_likedCommentIds',
          JSON.stringify([...likedCommentIds, commentId])
        );

        trackContentAction({
          ...sharedTrackingInfo,
          subunit_type: 'comments',
          item_type: 'button',
          item_name: 'heart',
          position_in_subunit: 4,
          action_type: 'react',
          action_value: 'heart',
        });
      } else {
        // error state?
      }
    } catch (error) {
      console.error('Error posting like', error);
    }
  };

  const handleDoubleTapClick = useDoubleTapHandler((state) => {
    handleLike(state);
  });

  const bindGestureHandlers = useGesture({
    onClick: handleDoubleTapClick,
  });

  // Handles modal animation state changes
  const handleModalAnimationStateChange = useCallback(
    (newState) => {
      if (newState === MODAL_STATES.START_BLUR) {
        setShouldBlurContent(true);

        if (hasAnimationCompleted) {
          // Skip animation for returning users
          setIsImageAnimating(false);
          setIsImageAnimatingBack(false);
        } else {
          // Start animation for first-time users
          setIsImageAnimating(true);
          setIsImageAnimatingBack(false);
        }
      } else if (newState === MODAL_STATES.END_BLUR) {
        setShouldBlurContent(false);
        setHasAnimationCompleted(true);
      } else if (newState === MODAL_STATES.REVERSING) {
        setIsImageAnimating(false);
        setIsImageAnimatingBack(true);
        setShouldBlurContent(false);
      }
    },
    [hasAnimationCompleted]
  );

  // Reset animation states when modal fully closes
  useEffect(() => {
    if (!isVisible && !shouldShow) {
      setTimeout(() => {
        setIsImageAnimating(false);
        setIsImageAnimatingBack(false);
        setShouldBlurContent(false);
        setHasAnimationCompleted(false);
      }, ANIMATION_TIMINGS.MODAL_CLOSE_ANIMATION);
    }
  }, [isVisible, shouldShow]);

  // shared across all events for the artifact.
  // though unit_type and name should be coming from island app if within it.
  const sharedTrackingInfo = {
    unit_type: 'artifact',
    unit_name: contentObject.id,
    subunit_type: 'artifact',
    subunit_name: contentObject.id,
    ...baseTrackingInfo, // always set priority for values from container
  };

  useEffect(() => {
    setLikeCount(contentObject?.content?.single_comment?.love_count);

    const hasLikedFromStorage =
      JSON.parse(localStorage.getItem('bfIsland_likedContentIds'))?.includes(
        contentObject?.id
      ) || false;

    if (hasLikedFromStorage) {
      setHasLiked(true);
    }

    const checkReportStatus = () => {
      if (typeof window !== 'undefined') {
        const reportedIds = JSON.parse(
          localStorage.getItem('reportedContentIds') || '[]'
        );
        setIsContentReported(reportedIds.includes(String(contentObject?.id)));
      }
    };

    checkReportStatus();

    const handleAppEvents = (event) => {
      if (event.detail?.action === 'visibilityStatus') {
        const visible = event.detail?.params?.isVisible || false;

        if (visible) {
          checkReportStatus();
        }
      }
    };

    window.addEventListener('appEvent', handleAppEvents);

    // Initialize feedback modal for this artifact
    let cleanupFunction = null;
    if (contentObject?.id) {
      cleanupFunction = triggerModal(contentObject.id);
    }

    return () => {
      window.removeEventListener('appEvent', handleAppEvents);
      if (typeof cleanupFunction === 'function') {
        cleanupFunction();
      }
    };
  }, [contentObject?.id, triggerModal]);

  useEffect(() => {
    getComments().then((commentsCount) => {
      setCommentsCount(commentsCount);
    });
  }, []);

  const getComments = async () => {
    try {
      const response = await fetch(
        `/comments-api/v1/comments?parent_id=${commentId}&page_size=20&child_size=10&sort=date&state=visible&type=text`
      );
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      return data?.count || 0;
    } catch (error) {
      console.error('Error fetching comments:', error);
      return 0;
    }
  };

  const onOpenShare = () => {
    if (openShare) {
      triggerEnhancedWebviewFeature({
        feature: 'navigationVisibility',
        params: {
          isVisible: false,
        },
        version: 1,
      });

      openShare({
        callbacks: {
          // restore nav when closing share dialog
          onDismiss: () => {
            triggerEnhancedWebviewFeature({
              feature: 'navigationVisibility',
              params: {
                isVisible: true,
              },
              version: 1,
            });
          },
        },
        options: {
          type: 'artifact',
          source: 'artifact',
          id: contentObject.id,
          imageUrl: contentObject?.content?.promotion?.thumbnail?.url,
        },
      });
    }
  };

  const bioClickTrackingData = {
    ...sharedTrackingInfo,
    item_type: 'text',
    item_name: 'user',
    target_content_type: 'user',
    target_content_id: ownerProfile.user_uuid,
    position_in_subunit: 0,
  };

  const { setObservable: setBioClickObservable } = useViewImpression({
    trackingData: bioClickTrackingData,
  });

  const handleBioClick = () => {
    const profileId = ownerProfile.user_id;

    if (isWeb) {
      trackInternalLink(bioClickTrackingData);
      router.push(`/profile/${profileId}`);
      return;
    }

    triggerEnhancedWebviewFeature({
      feature: 'navigate',
      params: {
        path: `/profile/${profileId}`,
      },
      version: 1,
    });
    trackInternalLink(bioClickTrackingData);
  };

  const footerUserNameClickTrackingData = {
    ...sharedTrackingInfo,
    item_type: 'text',
    item_name: 'user',
    target_content_type: 'user',
    target_content_id: ownerProfile.user_uuid,
    position_in_subunit: 5,
  };

  const handleFooterUserNameClick = () => {
    const profileId = ownerProfile.user_id;
    if (isWeb) {
      trackInternalLink(footerUserNameClickTrackingData);
      router.push(`/profile/${profileId}`);
      return;
    }
    triggerEnhancedWebviewFeature({
      feature: 'navigate',
      params: {
        path: `/profile/${profileId}`,
      },
      version: 1,
    });
    trackInternalLink(footerUserNameClickTrackingData);
  };

  const widgetClickMakeYoursTrackingData = {
    ...sharedTrackingInfo,
    item_type: 'button',
    item_name: 'make_yours',
    target_content_type: 'widget',
    target_content_id: contentObject?.content?.single_comment?.content_id, // content object ID of the BFP widget
    position_in_subunit: 1,
  };

  const { setObservable: setWidgetClickMakeYoursObservable } =
    useViewImpression({
      trackingData: widgetClickMakeYoursTrackingData,
    });

  const handleWidgetClick = (type, event) => {
    if (event) {
      event.stopPropagation();
    }

    if (isWeb) {
      openGatingModal(type);
      return;
    }

    triggerEnhancedWebviewFeature({
      feature: 'navigate',
      params: {
        path: `/webview/feed/content-objects/bfp_content/${contentObject?.content?.single_comment?.content_id}`,
        queryParams: {
          source: 'artifact',
          type,
        },
      },
      version: 1,
    });
    if (type === 'make_yours') {
      trackInternalLink(widgetClickMakeYoursTrackingData);
    } else if (type === 'made_with') {
      trackInternalLink({
        ...widgetClickMakeYoursTrackingData,
        item_name: 'made_with',
        position_in_subunit: 2,
      });
    }
  };

  const handleRiffClick = () => {
    trackContentAction({
      ...sharedTrackingInfo,
      item_type: 'button',
      item_name: 'riff',
      position_in_subunit: 4,
      action_type: 'open',
      action_value: 'riff',
    });
    onOpenShare();
  };

  const handleCommentMarqueeClick = () => {
    triggerEnhancedWebviewFeature({
      feature: 'commentsTray',
      params: { direction: 'open' },
      version: 1,
    });
    trackContentAction({
      ...sharedTrackingInfo,
      subunit_type: 'comments',
      item_type: 'button',
      item_name: 'comments',
      position_in_subunit: 3,
      action_type: 'open',
      action_value: 'comments',
    });
  };

  const handleExpandCaption = () => {
    triggerEnhancedWebviewFeature({
      feature: 'commentsTray',
      params: { direction: 'open' },
      version: 1,
    });
  };

  const openGatingModal = (trigger) => {
    trackContentAction({
      unit_type: 'artifact',
      unit_name: contentObject.id,
      subunit_type: 'artifact',
      subunit_name: contentObject.id,
      item_type: 'button',
      item_name: trigger,
      position_in_unit: 0,
      position_in_subunit: 1,
      action_type: 'open',
      action_value: 'unlock_download',
    });
    setIsGatingModalOpen(true);
  };

  const handleReportContentClick = () => {
    triggerEnhancedWebviewFeature({
      feature: 'navigate',
      params: {
        path: '/webview/report',
        queryParams: {
          contentType: 'artifact',
          contentId: String(contentObject.id),
          contentName:
            contentObject?.content?.bfp?.data?.intro?.card?.title || '',
        },
      },
      version: 1,
    });
  };

  const handleCreateStickerClick = useCallback(async () => {
    if (!createStickerImageUrl) {
      return;
    }

    try {
      const response = await fetch(createStickerImageUrl);
      if (!response.ok) {
        throw new Error(`Failed to fetch image: ${response.status}`);
      }

      const blob = await response.blob();
      const image = await readBlobAsBase64(blob);
      const source = buildCreateStickerSource({ contentObject, feedContent });
      const params = source ? { image, source } : { image };

      triggerEnhancedWebviewFeature({
        action: 'createSticker',
        params,
        version: 1,
      });
    } catch (error) {
      console.error('Error creating sticker', error);
    }
  }, [contentObject, createStickerImageUrl, feedContent]);

  const blurb = contentObject?.content?.single_comment?.blurb;

  // const bfp = contentObject?.content?.bfp;
  // const bfpType = bfp?.data?.type;
  // const isImageFilterArtifact = bfpType === 'image_transform';
  // const pillImage = "https://img.buzzfeed.com/store-an-image-prod-us-east-1/V4o2UI1HE.png"

  const madeWithText = contentObject?.content?.bfp?.data?.intro?.card?.title;
  const isDiscoverMode = feedContent?.type === 'discover';
  const CAPTION_COLLAPSE_LENGTH = 80;
  const captionIsTruncatable = blurb && blurb.length > CAPTION_COLLAPSE_LENGTH;
  const displayedCaption =
    isDiscoverMode && !isCaptionExpanded && captionIsTruncatable
      ? blurb.slice(0, CAPTION_COLLAPSE_LENGTH)
      : blurb;

  return (
    <div
      className={`${styles.artifact} artifact ${shouldBlurContent ? styles.contentBlurred : ''} ${isWeb ? styles.web : ''}`}
    >
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" />
      <link
        href="https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&display=swap"
        rel="stylesheet"
      />
      <div className={styles.background} />
      <div className={styles.bio}>
        <div
          className={`${styles.displayName} ${isDiscoverMode ? styles.centered : ''}`}
          onClick={handleBioClick}
          ref={(obvEl) => setBioClickObservable(obvEl)}
        >
          <img src={ownerProfile.avatar} alt="" className={styles.avatar} />
          <div className={styles.displayNameAndText}>
            <div>{ownerProfile.display_name}</div>
            {madeWithText && !isDiscoverMode && (
              <div
                className={styles.madeWithText}
                onClick={(e) => handleWidgetClick('made_with', e)}
              >
                <span>Made With:</span>
                <span>{madeWithText}</span>
              </div>
            )}
          </div>
        </div>
        {showReportButton && (
          <OverflowActions
            actions={[
              {
                id: 'report',
                text: isContentReported ? 'Content Reported' : 'Report Content',
                icon: <BFIslandDangerIcon fill="white" />,
                onClick: handleReportContentClick,
                bgColor: isContentReported ? 'rgba(46, 46, 46, 1)' : '#FF0000',
                color: isContentReported ? 'rgba(255, 255, 255, 1)' : '#FFFFFF',
                disabled: isContentReported,
              },
            ]}
          />
        )}
      </div>

      <div className={styles.imageWrapper}>
        {/* Gesture handlers on wrapper div to capture double-tap before react-zoom-pan-pinch processes events */}
        <div
          className={`${styles.imageAndBannerWrapper} ${
            // Final state when modal is open and animation finished
            isVisible && !isImageAnimating && !isImageAnimatingBack
              ? styles.finalState
              : // Animation states
                isImageAnimating
                ? styles.animatingToModal
                : isImageAnimatingBack
                  ? styles.animatingBack
                  : ''
          } ${isChallengeWinner ? styles.challengeWinner : ''}`}
          {...bindGestureHandlers()}
        >
          <ZoomableImage
            src={contentObject?.content?.single_comment?.picture_url}
            alt=""
            className={styles.artifactImage}
          />
          {type === 'community_challenge' && (
            <CommunityChallengeBanner
              isResolved={isResolved}
              isChallengeWinner={isChallengeWinner}
            />
          )}
        </div>

        {/* Feedback Modal handles all animation and rendering */}
        {shouldShow && (
          <FeedbackModal
            isVisible={isVisible}
            onClose={hideModal}
            title="Tell us what you're into."
            subtitle="We'll give you more of that good stuff."
            imageUrl={contentObject?.content?.single_comment?.picture_url}
            artifactId={contentObject?.id}
            onAnimationStateChange={handleModalAnimationStateChange}
            skipAnimation={hasAnimationCompleted}
            trackingInfo={sharedTrackingInfo}
          />
        )}
      </div>
      <div className={styles.footerWrapper}>
        {feedContent?.type === 'discover' && (
          <button
            className={styles.riffButton}
            onClick={isWeb ? () => openGatingModal('share') : handleRiffClick}
          >
            <RiffIcon />
            <span>Riff</span>
          </button>
        )}
        <BFIslandFooter
          blurb={isDiscoverMode ? null : blurb}
          userName={ownerProfile.display_name}
          onExpandCaption={
            isWeb
              ? () => openGatingModal('expand_caption')
              : handleExpandCaption
          }
          commentsCount={commentsCount}
          likeCount={likeCount}
          onLikeClick={isWeb ? () => openGatingModal('heart') : handleLike}
          hasLiked={hasLiked}
          onCommentClick={
            isWeb
              ? () => openGatingModal('comments')
              : handleCommentMarqueeClick
          }
          isShareEnabled={true}
          onShareClick={isWeb ? () => openGatingModal('share') : onOpenShare}
          onUserNameClick={handleFooterUserNameClick}
        />
        {showCreateStickerButton && (
          <button
            type="button"
            className={styles.createStickerButton}
            onClick={handleCreateStickerClick}
            aria-label="Create sticker"
            data-testid="create-sticker-button"
          >
            <CreateStickerIcon />
          </button>
        )}
      </div>
      {isDiscoverMode && blurb && (
        <div className={styles.discoverCaption}>
          <strong>{ownerProfile.display_name}</strong>{' '}
          {displayedCaption}
          {captionIsTruncatable && !isCaptionExpanded && (
            <button
              className={styles.captionMore}
              onClick={() => setIsCaptionExpanded(true)}
            >
              {' '}more
            </button>
          )}
        </div>
      )}
      {/* Heart animation portal */}
      {portal}
      {isDiscoverMode ? (
        <div className={styles.madeWithLinkWrapper}>
          {madeWithText && (
            <button
              className={styles.madeWithLinkButton}
              onClick={(e) => handleWidgetClick('made_with', e)}
              ref={(obvEl) => setWidgetClickMakeYoursObservable(obvEl)}
            >
              <span>Made with:</span>
              <span>{madeWithText}</span>
            </button>
          )}
        </div>
      ) : (
        <div className={styles.buttonWrapper}>
          <button
            className={styles.button}
            onClick={() => handleWidgetClick('make_yours')}
            ref={(obvEl) => setWidgetClickMakeYoursObservable(obvEl)}
          >
            {contentObject?.content?.bfp?.data?.intro?.card?.buttonCTA}
          </button>
        </div>
      )}
    </div>
  );
};

export default React.memo(Artifact);
